/**
 * Labelled spec items for the quick add popup and the product details page, in the design order:
 * pack size, shipping class, weight, dimensional sum and volume. Zero / empty values are left out.
 *
 * Values come from the product model (getPacksize, getWeight, ...) so variable products resolve
 * the selected variation and bundles their component totals. Pack size is free text shown as-is.
 */
function productSpecItems(values, { labels = {}, units = {}, format = (value) => value } = {}) {
    if (!values) return [];

    const number = (value) => Number(value) || 0;
    const packsize = typeof values.packsize === "string" ? values.packsize.trim() : "";
    const weight = number(values.weight);
    const dimensionalSum = number(values.dimensionalSum);
    const volume = number(values.volume);
    const items = [];

    if (packsize) {
        items.push({ key: "packsize", label: labels.packsize ?? "Pack Size", value: packsize });
    }
    if (values.shippingClass) {
        items.push({ key: "shipping", label: labels.shippingClass ?? "Shipping Class", value: values.shippingClass });
    }
    if (weight) {
        items.push({ key: "weight", label: labels.weight ?? "Weight", value: `${format(weight)}${units.kg ?? "kg"}` });
    }
    if (dimensionalSum) {
        items.push({
            key: "dimension",
            label: labels.dimensionalSum ?? "Dimensional Sum",
            value: `${format(dimensionalSum)}${units.m ?? "m"}`,
        });
    }
    if (volume) {
        items.push({ key: "volume", label: labels.volume ?? "Volume", value: `${format(volume)}${units.m3 ?? "m³"}` });
    }

    return items;
}

/**
 * Spec values from a product model. Variable products read the selected variation (getPacksize /
 * getWeight / ... delegate to it); bundles return their component totals and have no pack size.
 */
function productSpecValues(product) {
    if (!product) return null;

    const variation = typeof product.getSelectedVariation === "function" ? product.getSelectedVariation() : null;
    const source = variation ?? product;

    return {
        packsize: typeof product.getPacksize === "function" ? product.getPacksize() : product.packsize,
        shippingClass: source?.shipping_class?.name ?? (typeof source?.shipping_class === "string" ? source.shipping_class : null),
        weight: product.getWeight?.(),
        dimensionalSum: product.getDimensionalSum?.(),
        volume: product.getVolume?.(),
    };
}

/** Spec labels from the frontend product translations (web/frontend/product.php). */
function productSpecLabels(translations = {}) {
    return {
        packsize: translations.packsize,
        shippingClass: translations.shipping_class,
        weight: translations.spec_weight,
        dimensionalSum: translations.spec_dimensional_sum,
        volume: translations.spec_volume,
    };
}

module.exports = { productSpecItems, productSpecValues, productSpecLabels };
